import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Position, Stuecklistengruppe } from '../api/typen'

const EBENEN = [
  { schluessel: 'standort', beschriftung: 'Standort' },
  { schluessel: 'versorgungspunkt', beschriftung: 'Versorgungspunkt' },
  { schluessel: 'schrank', beschriftung: 'Schrank' },
] as const

/**
 * P10-01: aggregierte Stueckliste, wahlweise je Standort, Versorgungspunkt oder Schrank. Als eigenes
 * Bauteil, weil sie sowohl auf der mobilen Ergebnisseite als auch im Buero erscheint.
 */
export default function StuecklisteInhalt({ erfassungId }: { erfassungId: string }) {
  const [ebene, setEbene] = useState<(typeof EBENEN)[number]['schluessel']>('standort')

  const stueckliste = useQuery({
    queryKey: ['stueckliste', erfassungId, ebene],
    queryFn: () =>
      api.get<Stuecklistengruppe[]>(`/api/erfassungen/${erfassungId}/stueckliste?ebene=${ebene}`),
  })

  return (
    <div className="gruppe" style={{ gap: 16 }}>
      <div className="tabs" role="tablist" aria-label="Aggregationsebene">
        {EBENEN.map((eintrag) => (
          <button
            key={eintrag.schluessel}
            className="tab"
            role="tab"
            aria-selected={ebene === eintrag.schluessel}
            onClick={() => setEbene(eintrag.schluessel)}
          >
            {eintrag.beschriftung}
          </button>
        ))}
      </div>

      {stueckliste.isPending && <div className="spinner" />}

      {stueckliste.data?.length === 0 && (
        <p className="sek">Für diese Ebene gibt es noch keine Ergebnisse.</p>
      )}

      {stueckliste.data?.map((gruppe) => (
        <section key={gruppe.kennung} className="gruppe">
          <div className="kopfzeile">
            <span className="label">{gruppe.bezeichnung}</span>
            <span className="sek">{gruppe.kennung}</span>
          </div>

          {gruppe.artikelgruppen.every((eintrag) => eintrag.summe === 0) &&
          gruppe.smartScales.every((eintrag) => eintrag.menge === 0) ? (
            <p className="sek">Noch keine bestätigten oder vorgeschlagenen Ergebnisse.</p>
          ) : (
            <>
              <Gesamtmengen gruppe={gruppe} />
              <table className="liste">
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th>Artikelnummer</th>
                    <th style={{ textAlign: 'right' }}>Menge</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Bestellt wird immer Box H8, Box H16 oder ohne Box für eine Smart Scale, dazu
                    der FiFo-Einsatz je Smart Scale. Je Artikel eine Kopfzeile mit Summe, darunter
                    alle Varianten - auch leere, damit jede Artikelnummer sichtbar bleibt. */}
                  {gruppe.artikelgruppen.map((eintrag) => (
                    <Fragment key={`${eintrag.kategorie}-${eintrag.artikel}`}>
                      <tr className="zwischenkopf">
                        <td>{eintrag.bezeichnung}</td>
                        <td className="sek">{eintrag.varianten.length} Varianten</td>
                        <td className="n">Summe {eintrag.summe}</td>
                      </tr>
                      {eintrag.varianten.map((variante) => (
                        <Zeile key={`${variante.kategorie}-${variante.variante}`} position={variante} />
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      ))}

      <p className="sek">
        Gezählt werden bestätigte Ergebnisse; für noch offene Fächer zählt ersatzweise der Vorschlag des
        Systems. Jede Waage zählt als Box H8, Box H16 oder ohne Box in der Variante für ihre Größe; eine Waage
        ohne Boxangabe zählt noch nicht. FiFo-Einsätze zählen in der Höhe der Box, bei „ohne Box“ in der Höhe
        des Korbs (flach H8, tief H16); ohne beide Angaben zählen sie noch nicht. Die Übersicht der Smart
        Scales zählt jede Waage einmal, unabhängig von der Box, und ist keine eigene Bestellposition.
        Sonderfälle zählen mit ihrer manuellen Zuordnung und stehen auf Schrankebene unter ihrer eigenen
        Kennung.
      </p>
    </div>
  )
}

/**
 * Alle Smart Scales je Größe ohne Aufteilung nach Box und die FiFo-Einsätze je Richtung ohne
 * Aufteilung nach Größe (Iteration 9, wie im Mockup). Eigene Tabelle ohne Artikelnummer, weil das
 * keine Bestellpositionen sind – sie stecken bereits in den Artikelzeilen darunter.
 */
function Gesamtmengen({ gruppe }: { gruppe: Stuecklistengruppe }) {
  const summe = gruppe.smartScales.reduce((gesamt, eintrag) => gesamt + eintrag.menge, 0)
  const mitBox = gruppe.artikelgruppen
    .filter((eintrag) => eintrag.kategorie === 'box')
    .reduce((gesamt, eintrag) => gesamt + eintrag.summe, 0)
  const einsaetze = gruppe.artikelgruppen.filter((eintrag) => eintrag.kategorie === 'fifo')

  return (
    <table className="liste">
      <thead>
        <tr>
          <th>Gesamtmengen ohne Aufteilung</th>
          <th style={{ textAlign: 'right' }}>Menge</th>
        </tr>
      </thead>
      <tbody>
        <tr className="zwischenkopf">
          <td>Smart Scales gesamt</td>
          <td className="n">Summe {summe}</td>
        </tr>
        {gruppe.smartScales.map((eintrag) => (
          <tr key={eintrag.waage} className="variante">
            <td>{eintrag.bezeichnung}</td>
            <td className={eintrag.menge === 0 ? 'n leer' : 'n'}>
              {eintrag.menge === 0 ? '–' : eintrag.menge}
            </td>
          </tr>
        ))}
        {summe > mitBox && (
          <tr>
            <td colSpan={2} className="sek">
              davon {summe - mitBox} noch ohne Boxangabe – in den Artikeln unten noch nicht gezählt
            </td>
          </tr>
        )}
        {/* FiFo-Einsätze je Richtung und Höhe über alle Smart-Scale-Größen hinweg; die Aufteilung
            je Größe steht unten bei den Artikeln. */}
        {einsaetze.map((eintrag) => (
          <tr key={eintrag.artikel} className="zwischenkopf">
            <td>{eintrag.bezeichnung} gesamt</td>
            <td className="n">Summe {eintrag.summe}</td>
          </tr>
        ))}
        {gruppe.fifoOhneHoehe > 0 && (
          <tr>
            <td colSpan={2} className="sek">
              dazu {gruppe.fifoOhneHoehe} FiFo-Einsätze noch ohne Höhenangabe – in den Artikeln unten noch
              nicht gezählt
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

function Zeile({ position }: { position: Position }) {
  return (
    <tr className="variante">
      <td>{position.bezeichnung}</td>
      <td className="sek">{position.artikelnummer}</td>
      <td className={position.menge === 0 ? 'n leer' : 'n'}>{position.menge === 0 ? '–' : position.menge}</td>
    </tr>
  )
}
